import { cva } from 'class-variance-authority';
import cn from 'clsx';
import { PropsWithChildren, ReactNode } from 'react';

const descriptionContainer = cva('flex w-full flex-col items-center p-normal-large', {
  variants: {
    rounded: {
      true: 'rounded-bzc-large'
    }
  },
  defaultVariants: {
    rounded: true
  }
});

export interface DescriptionProps {
  bg?: string;
  graphic?: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  info?: ReactNode;
  button?: ReactNode;
  size?: 'medium' | 'small';
  className?: string;
}

const _Title = ({ children, size }: PropsWithChildren<Pick<DescriptionProps, 'size'>>) => (
  <div
    className={cn('whitespace-pre-wrap text-center text-neutral-foreground-main', {
      'heading-small-bold': size === 'medium',
      'body-large-bold': size === 'small'
    })}
  >
    {children}
  </div>
);

const _ButtonWrapper = ({ children, size }: PropsWithChildren<Pick<DescriptionProps, 'size'>>) => (
  <div
    className={cn('pb-normal-medium w-full flex justify-center', {
      'pt-tiny-large': size === 'medium',
      'pt-tiny-medium': size === 'small'
    })}
  >
    {children}
  </div>
);

const _Info = ({ children, bg }: PropsWithChildren<Pick<DescriptionProps, 'bg'>>) => (
  <div className={'flex w-full justify-center py-normal-small'}>
    <div
      className={cn('body-small-regular flex items-center justify-center rounded-[1000px] px-normal-medium py-tiny-large text-center', {
        'bg-neutral-background-high': bg,
        'bg-neutral-background-lowest': !bg
      })}
    >
      {children}
    </div>
  </div>
);

const _InnerDescription = ({ children, size }: PropsWithChildren<Pick<DescriptionProps, 'size'>>) => {
  return (
    <div
      className={cn('whitespace-pre-wrap text-center text-neutral-foreground-low', {
        'body-medium-regular': size === 'medium',
        'body-small-regular': size === 'small'
      })}
    >
      {children}
    </div>
  );
};

export const Description = ({ bg, graphic, title, info, button, size = 'medium', description, className, ...props }: DescriptionProps) => {
  return (
    <div className={cn(descriptionContainer({ rounded: !!bg }), className)} style={{ backgroundColor: bg || 'transparent' }} {...props}>
      {graphic}
      <div
        className={cn('flex flex-col gap-tiny-medium', {
          'py-normal-medium': size === 'medium',
          'py-normal-small': size === 'small'
        })}
      >
        {title && <_Title size={size}>{title}</_Title>}
        {info && <_Info bg={bg}>{info}</_Info>}
        {description && <_InnerDescription size={size}>{description}</_InnerDescription>}
      </div>
      {button && <_ButtonWrapper size={size}>{button}</_ButtonWrapper>}
    </div>
  );
};
